import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { EntryMethod } from 'components/enter/EntryMethod';
import { EnterByPostBlurb } from 'components/enter2026/EnterByPostBlurb';
import FaqCard from 'components/FaqCard';
import { WinnerCarousel } from 'components/home-page/winner-section/WinnerCarousel';
import MainLayout from 'components/layout/MainLayout';
import RoundedButton from 'components/RoundedButton';
import PlainTabControl from 'components/tab-control/PlainTabControl';
import { commonFaqs } from 'constants/FAQs';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { GetServerSideProps } from 'next';
import React, { ReactElement, useEffect, useState } from 'react';
import {
  getChooseYourPlanBoostValue,
  getFirstEntryVsFirstOrder50Off2026Value,
} from 'utils/ab-test/abTests';
import {
  getEntryMethodStickerContent,
  PricingTestGroup,
} from 'utils/ab-test/pricing/pricingTestUtils';
import { PRIZE_NUMBERS } from 'utils/constants';
import { fetchWinnersHomepage, WinnerHomepage } from 'utils/plasmicCMS';
import Celebration30 from '../components/svg/Celebration30.svg';
import Gift30 from '../components/svg/Gift30.svg';
import PoundInCircle30 from '../components/svg/PoundInCircle30.svg';
import Target30 from '../components/svg/Target30.svg';
import TickShield30 from '../components/svg/TickShield30.svg';
import Tree30 from '../components/svg/Tree30.svg';
import TrustStar30 from '../components/svg/TrustStar30.svg';
import { ChooseYourPlanOriginal } from './choose-your-plan-original';
import { ChooseYourPlanV1 } from './choose-your-plan-v1';
import styles from './choose-your-plan.module.css';

interface Props {
  winners: WinnerHomepage[];
  pricingTestGroup?: PricingTestGroup;
}

const ChooseYourPlanControl = (props: Props) => {
  const enterStore = useEnterStore();
  const heroImageDesktop =
    'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2025/choose-your-plan/HeroV1-desktop.jpg';
  const heroImageMobile =
    'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2025/choose-your-plan/HeroV1-mobile.jpg';

  return (
    <div className="aboutUsTypographyReset">
      <AboutUsModule background="white" withNoTopPadding withReducedPadding>
        <div
          className={styles.hero}
          style={{
            // See comment in choose-your-plan-module.css
            visibility: 'hidden',
          }}
        >
          {/* Use picture and source to decide which image to show on desktop vs mobile. We must ensure that the img element has width and height
          attributes, so that the browser knows what aspect ratio the image will be. The images on mobile vs desktop have different aspect ratios
          therefore there are two separate picture elements. The media queries that target the class decide which element to show */}
          <picture className={styles.heroImageDesktop}>
            <source
              srcSet="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
              media="(max-width: 767px)"
            />
            <source srcSet={heroImageDesktop} media="(min-width: 768px)" />
            <img src="" alt="Play YOUR way!" width="1250" height="525" />
          </picture>
          <picture className={styles.heroImageMobile}>
            <source srcSet={heroImageMobile} media="(max-width: 767px)" />
            <source
              srcSet="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
              media="(min-width: 768px)"
            />
            <img src="" alt="Play YOUR way!" width="1560" height="844" />
          </picture>
          <AboutUsSiteContainer>
            <div className={styles.heroTrustBulletContainer}>
              <ul
                className={
                  styles.trustBulletList + ' aboutUsTypographyBodySmall'
                }
              >
                <li className={styles.trustBulletListItem}>
                  <CircleWithDropShadowIcon icon={Target30} />
                  <span>Over £11 million in prizes won</span>
                </li>
                <li className={styles.trustBulletListItem}>
                  <CircleWithDropShadowIcon icon={Celebration30} />
                  <span>Hundreds of prizes to choose from</span>
                </li>
                <li className={styles.trustBulletListItem}>
                  <CircleWithDropShadowIcon icon={Gift30} />
                  <span>Win more often, prizes big & small</span>
                </li>
                <li className={styles.trustBulletListItem}>
                  <CircleWithDropShadowIcon icon={Tree30} />
                  <span>Plant a tree with every purchase</span>
                </li>
              </ul>
            </div>
          </AboutUsSiteContainer>
        </div>
      </AboutUsModule>
      <AboutUsModule background="white" withReducedPadding>
        <AboutUsSiteContainer>
          <h2 className="visuallyHidden">Entry methods</h2>
          <PlainTabControl
            pages={[
              {
                title: 'By Post',
                subtitle: 'no purchase necessary',
                content: (
                  <div className={styles.entryMethodTabPage}>
                    <EnterByPostBlurb />
                  </div>
                ),
              },
              {
                title: 'Online',
                content: (
                  <div className={styles.entryMethodTabPage}>
                    <h3 className="visuallyHidden">Online</h3>
                    <div className={styles.entryMethodCards}>
                      <div
                        className={
                          styles.entryMethodCard + ' withMinimalSpacing'
                        }
                      >
                        <div className={styles.entryMethodContent}>
                          <div className={styles.entryMethodHeading}>
                            <h4 className="aboutUsTypographyHeaderLarge">
                              Premium
                            </h4>
                            <span>from £2.95/ week</span>
                          </div>
                          <p>Most prizes and best odds</p>
                          <ul
                            className={
                              styles.entryMethodList +
                              ' aboutUsTypographyBodySmall'
                            }
                          >
                            <li>
                              Chance to win {PRIZE_NUMBERS.PREMIUM} prizes
                            </li>
                            <li>8x better odds than Lite*</li>
                            <li>
                              Cancel <strong>anytime</strong>
                            </li>
                          </ul>
                          <div className={styles.entryMethodSticker}>
                            <div className={styles.entryMethodStickerText}>
                              {getEntryMethodStickerContent(
                                props.pricingTestGroup,
                              )}
                            </div>
                          </div>
                          <span className={styles.entryMethodBadge}>
                            MOST POPULAR
                          </span>
                        </div>
                        <RoundedButton
                          href="/enternow/1"
                          rebrandDm2025
                          fullWidth
                          onClick={() => {
                            enterStore.setEntryMethod(
                              EntryMethod.PremiumSubscription,
                            );
                          }}
                        >
                          Get Premium
                        </RoundedButton>
                      </div>
                      <div
                        className={
                          styles.entryMethodCard + ' withMinimalSpacing'
                        }
                      >
                        <div className={styles.entryMethodContent}>
                          <div className={styles.entryMethodHeading}>
                            <h4 className="aboutUsTypographyHeaderLarge">
                              Lite
                            </h4>
                            <span>from £1.50/ week</span>
                          </div>
                          <p>Fewer prizes and tougher odds</p>
                          <ul
                            className={
                              styles.entryMethodList +
                              ' aboutUsTypographyBodySmall'
                            }
                          >
                            <li>Chance to win {PRIZE_NUMBERS.LITE} prizes</li>
                            <li>
                              Cancel <strong>anytime</strong>
                            </li>
                          </ul>
                        </div>
                        <RoundedButton
                          href="/enter?em=LiteSubscription"
                          rebrandDm2025
                          fullWidth
                        >
                          Get Lite
                        </RoundedButton>
                      </div>
                    </div>
                    <div className={styles.entryMethodAllPlans}>
                      <h3 className="aboutUsTypographyHeaderMedium">
                        All plans include
                      </h3>
                      <ul className={styles.entryMethodAllPlansList}>
                        <li>
                          <strong>Cancel anytime</strong> - you’re in control
                        </li>
                        <li>
                          Automatic entry - <strong>never miss a draw</strong>
                        </li>
                        <li>
                          <strong>Match numbers</strong> to unlock prizes
                        </li>
                        <li>Choose your prize when you win</li>
                        <li>
                          <strong>Exclusive</strong> member raffles & perks
                        </li>
                        <li>One tree planted per draw you enter</li>
                      </ul>
                      <hr className={styles.horizontalRule} />
                      <div className="withReducedSpacing">
                        <p className="aboutUsTypographyCaption aboutUsTypographyCentered">
                          *Valid for Bronze prizes and above (excluding Boost)
                        </p>
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
            initialActiveIndex={1}
          ></PlainTabControl>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <AboutUsModule background="grey" withReducedPadding>
        <AboutUsSiteContainer>
          <h2
            className={
              'aboutUsTypographyHeaderXlargeOnWhite' +
              ' ' +
              styles.prizePlatformTitle
            }
            data-text="A prize platform you can trust"
          >
            A prize platform you can trust
          </h2>
          <div className={styles.prizePlatformBulletContainer}>
            <ul
              className={
                styles.trustBulletList +
                ' ' +
                styles.trustBulletListQuarterOnMobile +
                ' ' +
                styles.trustBulletListStackOnMobile
              }
            >
              <li className={styles.trustBulletListItem}>
                <CircleWithDropShadowIcon icon={TrustStar30} />
                <span>4.5★ rating on Trustpilot</span>
              </li>
              <li className={styles.trustBulletListItem}>
                <CircleWithDropShadowIcon icon={Tree30} />
                <span>5m trees planted</span>
              </li>
              <li className={styles.trustBulletListItem}>
                <CircleWithDropShadowIcon icon={PoundInCircle30} />
                <span>
                  {/* {!drawStore.prizesWonValue || isNaN(drawStore.prizesWonValue)
                    ? 'Lots of'
                    : '£' +
                    (drawStore.prizesWonValue / 10000000000)
                      .toFixed(1)
                      .replace('.0', '') +
                    'm'}{' '} */}
                  £11m prizes already won
                </span>
              </li>
              <li className={styles.trustBulletListItem}>
                <CircleWithDropShadowIcon icon={TickShield30} />
                <span>Registered with Fundraising Regulator</span>
              </li>
            </ul>
          </div>
          <div
            /* Need overflow hidden, because the plasmic component spills out of its container */
            style={{ overflow: 'hidden' }}
          >
            <div>
              <WinnerCarousel winners={props.winners} />
            </div>
          </div>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <AboutUsModule background="white" withReducedPadding>
        <AboutUsSiteContainer>
          <h2 className="aboutUsTypographyHeaderXlargeOnWhite" data-text="FAQ">
            FAQ
          </h2>
          {faqs.map((x, index) => (
            <FaqCard
              key={index}
              rebrand2025
              question={x.question}
              answer={x.answer}
              withBorder
            ></FaqCard>
          ))}
        </AboutUsSiteContainer>
      </AboutUsModule>
    </div>
  );
};

interface Faq {
  question: string;
  answer: ReactElement;
}

const faqs: Faq[] = [
  commonFaqs.howDoesDaymadeWork,
  {
    question: 'How are there so many winners?',
    answer: (
      <p>
        With DAYMADE, you only need to match 1 number to start winning. Every
        week, we have over thousands of winners of prizes big and small: from
        tokens and treats to bucketlist holidays, home makeovers, and lifestyle
        prizes. Of course the odds of matching all 5 numbers are a lot worse
        than matching 1 or 2 numbers. While we've had a lot of lucky winners
        winning dream holidays, gadget bundles, luxury items & more, most of our
        winners win the smaller prizes like tokens and Bronze prizes. We try to
        make as many people’s day as we can. Match 1 number to win a token and
        match all 5 numbers to claim that amazing holiday or home makeover worth
        up to £100,000.
      </p>
    ),
  },
  {
    question: 'Can I buy a one-off ticket?',
    answer: (
      <p>
        For online entries, DAYMADE is a subscription that can be snoozed or
        cancelled any time. You can enter one-off via our Postal Entry Route.
      </p>
    ),
  },
  {
    question: 'What prizes can I win?',
    answer: (
      <p>
        You can win a diverse range of prizes, from tokens to holidays,
        experiences, lifestyle items, tech gadgets, and family-oriented prizes
        in our weekly draws. We have over {PRIZE_NUMBERS.PREMIUM} prizes on
        offer that are hand selected by our team, so there’s something for
        everyone.
      </p>
    ),
  },
  {
    question: 'Can I choose my prize?',
    answer: (
      <p>
        Yes! With over {PRIZE_NUMBERS.PREMIUM} prizes to choose from, there’s
        something for everyone. For each number you match correctly, you will
        unlock a larger prize tier. Our tiers include Bronze, Silver, Gold,
        Platinum, Diamond, and for our grand prize, the DAYMAKER tier. Each tier
        has a wide assortment of prizes to choose from.
      </p>
    ),
  },
  {
    question: 'How will I be notified when I win?',
    answer: (
      <p>
        We email all winners within two hours of the draw. If we don’t hear from
        you within 2 weeks, don’t worry, we will send you a reminder. You can
        also log-in to your account at any time and visit your account page to
        view any prizes that you have won.
      </p>
    ),
  },
  {
    question: 'What are tokens?',
    answer: (
      <p>
        Tokens are our smallest prize, which you can win by matching just 1
        number. You can then use these tokens to plant more trees or enter more
        prize draws on our site. Some people save up tokens over time in order
        to redeem a bigger prize.
      </p>
    ),
  },
  {
    question: 'Can I customise my holiday and bring a guest(s)?',
    answer: (
      <p>
        Definitely! Most trips are planned for 2 people, but we can tailor the
        number of travellers to suit your needs, as long as it fits within the
        prize budget. We also include unique activities and experiences to make
        your trip unforgettable. Just pick your dates, and we'll handle the
        rest. You can find out what's included in your chosen trip by checking
        the prize details.
      </p>
    ),
  },
];

const CircleWithDropShadowIcon = (props: { icon: React.ComponentType }) => {
  const IconComponent = props.icon;
  return (
    <span className={styles.circleWithDropShadow}>
      <span className={styles.circleWithDropShadowInner}>
        <IconComponent />
        <span className={styles.circleWithDropShadowAlt}>
          <IconComponent />
        </span>
      </span>
    </span>
  );
};

const ChooseYourPlanPage = (props: Props) => {
  const [abTest, setAbTest] = useState<
    ReturnType<typeof getChooseYourPlanBoostValue> | undefined
  >(undefined);
  const [pricingTestGroup, setPricingTestGroup] = useState<
    PricingTestGroup | undefined
  >(undefined);
  useEffect(() => {
    setAbTest(getChooseYourPlanBoostValue());
    setPricingTestGroup(getFirstEntryVsFirstOrder50Off2026Value());

    if (window.rdt) {
      window.rdt('track', 'ViewContent');
    }
  }, []);
  return (
    <MainLayout limitItemWidths>
      {abTest == undefined ? undefined : abTest === 'V5' ? (
        <ChooseYourPlanV1 {...props} pricingTestGroup={pricingTestGroup} />
      ) : abTest === 'ORIGINALV1' ? (
        <ChooseYourPlanOriginal
          {...props}
          litePrice="£1.50"
          premiumPrice="£2.95"
          pricingTestGroup={pricingTestGroup}
        />
      ) : abTest === 'ORIGINALV2' ? (
        <ChooseYourPlanOriginal
          {...props}
          litePrice="£2.00"
          premiumPrice="£3.45"
          isBoosted
          pricingTestGroup={pricingTestGroup}
        />
      ) : (
        <ChooseYourPlanControl {...props} pricingTestGroup={pricingTestGroup} />
      )}
    </MainLayout>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const [winners] = await Promise.all([fetchWinnersHomepage()]);

  return {
    props: {
      winners,
    },
  };
};

export default ChooseYourPlanPage;
